import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Calendar, ExternalLink, Newspaper, Share2 } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { ShareMenu } from '../components/ShareMenu';
import Header from '../components/Header';
import Footer from '../components/Footer';
import usePageTitle from '../hooks/usePageTitle';
import { enhanceAccessibleHtml, neutralizeInteractiveHtml } from '../utils/a11yHtml';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatNewsDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
};

const NewsSnippet = ({ snippet, className = '', neutralizeInteractive = false }) => {
  if (!snippet) return null;
  const isHtml = snippet.trim().startsWith('<');
  if (isHtml) {
    let safe = enhanceAccessibleHtml(snippet);
    if (neutralizeInteractive) {
      safe = neutralizeInteractiveHtml(safe);
    }
    return <div className={`prose prose-sm max-w-none text-inherit ${className}`} dangerouslySetInnerHTML={{ __html: safe }} />;
  }
  return <p className={className}>{snippet}</p>;
};

const NewsDetailDialog = ({ item, open, onOpenChange }) => {
  if (!item) return null;
  const imageUrl = item.thumbnail_url?.startsWith('/') ? `${BACKEND_URL}${item.thumbnail_url}` : item.thumbnail_url || 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=800';
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto p-0 gap-0 flex flex-col">
        <div className="relative aspect-[16/10] overflow-hidden bg-gray-100 rounded-t-lg flex-shrink-0">
          <img
            src={imageUrl}
            alt={item.thumbnail_alt || item.title}
            className="w-full h-full object-cover"
          />
          {item.is_featured && (
            <div className="absolute top-3 left-3">
              <Badge className="bg-[#C8473A] text-white">Featured</Badge>
            </div>
          )}
        </div>
        <div className="p-6 flex-1 bg-white rounded-b-lg relative z-10">
          <DialogHeader>
            <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
              <span>{item.source_name}</span>
              <span>·</span>
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {formatNewsDate(item.published_date)}
              </span>
            </div>
            <DialogTitle className="text-xl font-bold text-[#211F1F] text-left pr-12">
              {item.title}
            </DialogTitle>
          </DialogHeader>
          {item.snippet && (
            <NewsSnippet snippet={item.snippet} className="text-gray-600 mt-4 leading-relaxed" />
          )}
          <div className="flex flex-wrap items-center gap-3 mt-6">
            {item.source_url && (
              <a
                href={item.source_url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Read full article on ${item.source_name} (opens in new tab)`}
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#C8473A] text-white rounded-xl font-medium hover:bg-[#B74F40] transition-colors"
              >
                Read full article on {item.source_name}
                <ExternalLink className="w-4 h-4" aria-hidden="true" />
              </a>
            )}
            <ShareMenu
              url={item.source_url || (typeof window !== 'undefined' ? window.location.href : '')}
              title={item.title}
              description={typeof item.snippet === 'string' && item.snippet.trim().startsWith('<') ? item.snippet.replace(/<[^>]*>/g, '').trim() : item.snippet}
              trigger={
                <Button variant="outline" size="sm" className="gap-2">
                  <Share2 className="w-4 h-4" />
                  Share
                </Button>
              }
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const FeaturedNewsCard = ({ item, onClick }) => {
  return (
    <motion.button
      type="button"
      onClick={() => onClick?.(item)}
      aria-label={`View news: ${item.title}`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="group block w-full cursor-pointer text-left"
    >
      <div className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-[#211F1F] to-[#3d3a3a]">
        <div className="grid lg:grid-cols-2 gap-0">
          <div className="relative aspect-[16/10] lg:aspect-auto lg:min-h-[320px] overflow-hidden">
            <img
              src={item.thumbnail_url?.startsWith('/') ? `${BACKEND_URL}${item.thumbnail_url}` : item.thumbnail_url || 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=800'}
              alt={item.thumbnail_alt || item.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent lg:hidden" />
          </div>
          <div className="p-8 lg:p-12 flex flex-col justify-center">
            <div className="flex items-center gap-3 mb-4">
              <Badge className="bg-[#C8473A] text-white hover:bg-[#C8473A]">Featured</Badge>
              <span className="text-gray-400 text-sm">{item.source_name}</span>
            </div>
            <h2 className="text-2xl lg:text-3xl font-bold text-white mb-4 group-hover:text-[#C8473A] transition-colors line-clamp-3">
              {item.title}
            </h2>
            {item.snippet && (
              <div className="text-gray-300 mb-6 line-clamp-2 text-base lg:text-lg">
                <NewsSnippet snippet={item.snippet} neutralizeInteractive />
              </div>
            )}
            <div className="flex items-center gap-4 text-sm text-gray-400 mb-6">
              <span className="flex items-center gap-1.5">
                <Calendar className="w-4 h-4" />
                {formatNewsDate(item.published_date)}
              </span>
            </div>
            <span className="inline-flex items-center gap-2 text-white font-medium group-hover:text-[#C8473A] transition-colors">
              Read on {item.source_name}
              <ExternalLink className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </span>
          </div>
        </div>
      </div>
    </motion.button>
  );
};

const NewsCard = ({ item, index, onClick }) => {
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
    >
      <motion.button
        type="button"
        onClick={() => onClick?.(item)}
        aria-label={`View news: ${item.title}`}
        className="group block h-full w-full cursor-pointer text-left"
      >
        <article className="bg-white rounded-2xl overflow-hidden border border-gray-100 hover:border-[#C8473A]/20 hover:shadow-xl transition-all duration-300 h-full flex flex-col">
          <div className="relative aspect-[16/10] overflow-hidden bg-gray-100">
            <img
              src={item.thumbnail_url?.startsWith('/') ? `${BACKEND_URL}${item.thumbnail_url}` : item.thumbnail_url || 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=600'}
              alt={item.thumbnail_alt || item.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
            {item.is_featured && (
              <div className="absolute top-3 left-3">
                <Badge className="bg-[#C8473A] text-white">Featured</Badge>
              </div>
            )}
          </div>
          <div className="p-5 flex-1 flex flex-col">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xs text-gray-500">{item.source_name}</span>
              <span className="text-xs text-gray-500">•</span>
              <span className="text-xs text-gray-500">{formatDate(item.published_date)}</span>
            </div>
            <h2 className="heading-card-title group-hover:text-[#C8473A] transition-colors mb-2 line-clamp-2">
              {item.title}
            </h2>
            {item.snippet && (
              <div className="text-sm text-gray-500 mb-4 line-clamp-2 flex-1">
                <NewsSnippet snippet={item.snippet} neutralizeInteractive />
              </div>
            )}
            <span className="inline-flex items-center gap-1.5 text-sm font-medium text-[#C8473A]">
              Read article
              <ExternalLink className="w-3.5 h-3.5" />
            </span>
          </div>
        </article>
      </motion.button>
    </motion.div>
  );
};

const NewsPage = () => {
  usePageTitle('News');
  const location = useLocation();
  const navigate = useNavigate();
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedNews, setSelectedNews] = useState(null);

  const openId = new URLSearchParams(location.search).get('open');

  useEffect(() => {
    axios.get(`${API}/admin/news/public/list?limit=50`)
      .then(res => setNews(Array.isArray(res.data) ? res.data : []))
      .catch(() => setNews([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!openId || loading || selectedNews) return;
    const match = news.find((n) => String(n.article_id) === String(openId) || String(n.id) === String(openId));
    if (match) setSelectedNews(match);
  }, [openId, loading, news, selectedNews]);

  const handleOpen = (item) => {
    setSelectedNews(item);
    const id = item?.article_id || item?.id;
    if (id) navigate(`/news?open=${encodeURIComponent(id)}`, { replace: true });
  };

  const handleDialogOpenChange = (open) => {
    if (open) return;
    setSelectedNews(null);
    if (openId) navigate('/news', { replace: true });
  };

  // API returns reverse chronological (newest published first). Hero = newest featured item; grid = rest in same order.
  const featuredItem = news.find((n) => n.is_featured) ?? null;
  const featuredKey = featuredItem
    ? String(featuredItem.article_id ?? featuredItem.id ?? '')
    : '';
  const otherNews = featuredItem
    ? news.filter(
        (n) => String(n.article_id ?? n.id ?? '') !== featuredKey
      )
    : news;

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Header />

      <main id="main-content" tabIndex={-1} className="pt-32 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-10"
          >
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-gray-500 hover:text-[#C8473A] mb-6 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" aria-hidden="true" />
              Back to Home
            </Link>
            <h1 className="text-4xl md:text-5xl font-bold text-[#211F1F] mb-4">
              <span className="text-[#C8473A]">News</span>
            </h1>
            <p className="text-lg text-gray-600">
              Latest news about our portfolio, fund announcements, and industry insights from the YourNest ecosystem.
            </p>
          </motion.div>

          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map(i => (
                <div key={i} className="bg-white rounded-2xl overflow-hidden border border-gray-100 animate-pulse">
                  <div className="aspect-[16/10] bg-gray-200"></div>
                  <div className="p-5">
                    <div className="h-4 bg-gray-100 rounded w-24 mb-3"></div>
                    <div className="h-6 bg-gray-200 rounded w-full mb-2"></div>
                    <div className="h-4 bg-gray-100 rounded w-3/4"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : news.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-16 bg-white rounded-2xl border border-gray-100"
            >
              <Newspaper className="w-14 h-14 mx-auto text-gray-300 mb-4" />
              <h2 className="heading-empty">No news yet</h2>
              <p className="text-gray-500">Check back later for updates.</p>
            </motion.div>
          ) : (
            <>
              {featuredItem && (
                <div className="mb-12">
                  <FeaturedNewsCard item={featuredItem} onClick={handleOpen} />
                </div>
              )}
              {otherNews.length > 0 && (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {otherNews.map((item, index) => (
                    <NewsCard key={item.article_id || item.id} item={item} index={index} onClick={handleOpen} />
                  ))}
                </div>
              )}
              <NewsDetailDialog
                item={selectedNews}
                open={!!selectedNews}
                onOpenChange={handleDialogOpenChange}
              />
            </>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default NewsPage;
